/**
 * Data Hub console requests — the Import & Run wizard, the ledger, QC/Heal,
 * the custom rubric and the read-only console tabs.
 *
 * This is the only place these endpoints are called from. Components dispatch
 * the thunks at the bottom of the file and keep their own view state (which
 * is why there is no reducer here, as with the reset thunks in DataHubSlice);
 * `data-hub-library/data/api.ts` keeps the payload types and pure helpers.
 *
 * Thunks throw rather than `rejectWithValue`, so `.unwrap()` hands callers the
 * same `message` ApiRequest produced and `describeError` reads it unchanged.
 *
 * Responses come back in EB's standard envelope
 * (`{content, response: {status, message}}`) — `apiRequest` returns
 * `response.data`, so unwrap `.content` the way the Redux slices do.
 */
import { createAsyncThunk } from "@reduxjs/toolkit";

import { computeDataHubRoScores } from "./DataHubSlice";
import {
  describeError,
  measureFor,
  type CategoryOption,
  type CustomMetric,
  type HealScope,
  type LedgerRun,
  type QaReport,
  type QcConflictGroup,
  type QcRunSummary,
  type RubricCategory,
  type RubricImportPlanEntry,
  type RunPhaseOutcome,
  type StoredFile,
} from "../../data-hub-library/data/api";
import {
  coerceDataset,
  datasetHeaders,
  getActiveDataset,
} from "../../data-hub-library/data/dataset";
import { RO_SCORES_ENABLED } from "../../data-hub-library/data/flags";
import type {
  BenchmarkMetric,
  PhaseKey,
  QcProbe,
  QcReport,
  CategoryScores,
  DataHubStats,
  Matrix,
  MatrixQuery,
  PhaseResult,
  PipelineRun,
  ProductionRow,
  RoScores,
  RunStatus,
  StagingRow,
} from "../../data-hub-library/data/types";
import type { ImportTemplate } from "../../data-hub-library/lib/header-aliases";
import {
  del as rawDel,
  get as rawGet,
  post as rawPost,
  put as rawPut,
  uploadFile as rawUploadFile,
} from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";

/*
 * Every Data Hub request carries the active dataset.
 *
 * These shadow the imported helpers rather than each of the ~40 call sites
 * passing the header itself. That is the point: a call site that forgets would
 * silently read production while the console displays synthetic, and the bug
 * would look like stale data rather than a missing header. There is no way to
 * forget if there is no per-call decision to make.
 *
 * Caller-supplied headers merge on top, so an individual request can still
 * override if it ever needs to.
 */
function withDataset(options?: any) {
  const base = datasetHeaders();
  return {
    ...options,
    headers: { ...base.headers, ...(options?.headers ?? {}) },
  };
}

const get = (url: string, options?: any) => rawGet(url, withDataset(options));

const post = (url: string, data?: any, encode?: boolean, options?: any) =>
  rawPost(url, data, encode, withDataset(options));

const uploadFile = (url: string, file: File) =>
  rawUploadFile(url, file, withDataset());

function unwrap<T>(res: any, fallback: T): T {
  return (res?.content ?? res ?? fallback) as T;
}

/** Backend returns phases pre-ordered; this only normalises nullables. */
function toPhase(raw: any): PhaseResult {
  return {
    phase: raw?.phase,
    status: raw?.status,
    count: raw?.count ?? undefined,
    error: raw?.error ?? null,
    reason: raw?.reason ?? null,
    extras: raw?.extras ?? null,
    group: raw?.group ?? null,
    started_at: raw?.started_at ?? null,
    finished_at: raw?.finished_at ?? null,
  };
}

function toRun(raw: any, index = 0): PipelineRun {
  return {
    id: String(raw?.id ?? ""),
    // The API has no display number; derive a stable descending one from list
    // position, which matches the "#4, #3, …" ordering the console shows.
    run_no: raw?.run_no ?? index,
    started_at: raw?.started_at ?? "",
    finished_at: raw?.finished_at ?? null,
    duration_ms: raw?.duration_ms ?? null,
    status: (raw?.status ?? "running") as RunStatus,
    source: raw?.source ?? null,
    triggered_by: raw?.triggered_by ?? null,
    dataset_kind: coerceDataset(raw?.dataset_kind),
    phases: Array.isArray(raw?.phases) ? raw.phases.map(toPhase) : [],
  };
}

/**
 * Re-read a retained import as a File, so it re-enters the wizard through
 * exactly the same path as a file picked from disk.
 *
 * Returning a File rather than a parsed sheet is deliberate: every downstream
 * step — parsing, hashing, upload — then behaves identically whichever tab the
 * file came from, and there is no second code path to keep in step.
 */
async function fetchStoredFileAsFile(stored: StoredFile): Promise<File> {
  // Through the shared client, not bare fetch: the access token lives in an
  // Authorization header that only `apiRequest` knows how to attach.
  const blob: Blob = await get(
    API_URLS.dataHubFileDownload(stored.storage_path),
    {
      responseType: "blob",
    }
  );
  return new File([blob], stored.filename, {
    type: blob.type || "application/octet-stream",
  });
}

/**
 * Upload a file so a run can be resumed without picking it again.
 *
 * Returns null when no object store is configured — the wizard still starts,
 * and resume-before-Import asks the user to re-select the file.
 */
async function uploadDataHubFile(file: File): Promise<StoredFile | null> {
  const res = await uploadFile(API_URLS.dataHubFiles(), file);
  const c = unwrap<any>(res, {}) ?? {};
  return c.file ?? null;
}

function ledgerBody(res: any): any {
  const status = res?.response?.status ?? res?.status;
  if (status === "failure" || status === "unauthorized") {
    throw new Error(
      res?.response?.message ?? res?.message ?? "Ledger request failed"
    );
  }
  return unwrap<any>(res, {});
}

async function listLedgerRuns(params?: {
  dataset?: string;
  limit?: number;
}): Promise<LedgerRun[]> {
  const res = await get(
    API_URLS.dataHubLedgerRuns({
      dataset: params?.dataset,
      limit: params?.limit ?? 50,
    })
  );
  const c = ledgerBody(res);
  return Array.isArray(c?.runs) ? c.runs : [];
}

async function fetchLedgerRun(runId: string): Promise<LedgerRun> {
  return ledgerBody(await get(API_URLS.dataHubLedgerRun(runId))) as LedgerRun;
}

async function openLedgerRun(input: {
  source_filename?: string;
  storage_path?: string | null;
  file_hash?: string | null;
  sheet_name?: string | null;
  row_count?: number | null;
  dataset?: string;
}): Promise<LedgerRun> {
  return ledgerBody(
    await post(API_URLS.dataHubLedgerRuns(), {
      source_filename: input.source_filename ?? null,
      storage_path: input.storage_path ?? null,
      file_hash: input.file_hash ?? null,
      sheet_name: input.sheet_name ?? null,
      row_count: input.row_count ?? null,
      dataset: input.dataset ?? getActiveDataset(),
    })
  ) as LedgerRun;
}

async function saveLedgerDecisions(
  runId: string,
  decisions: Record<string, unknown>
): Promise<LedgerRun> {
  return ledgerBody(
    await post(API_URLS.dataHubLedgerDecisions(runId), { decisions })
  ) as LedgerRun;
}

async function beginLedgerStep(
  runId: string,
  step: string
): Promise<LedgerRun> {
  return ledgerBody(
    await post(API_URLS.dataHubLedgerStepBegin(runId, step), {})
  ) as LedgerRun;
}

async function succeedLedgerStep(
  runId: string,
  step: string,
  close?: {
    outcome_counts?: Record<string, unknown>;
    artifact_evidence?: Record<string, unknown>;
  }
): Promise<LedgerRun> {
  return ledgerBody(
    await post(API_URLS.dataHubLedgerStepSucceed(runId, step), {
      outcome_counts: close?.outcome_counts ?? null,
      artifact_evidence: close?.artifact_evidence ?? null,
    })
  ) as LedgerRun;
}

async function killLedgerRun(
  runId: string,
  reason?: string
): Promise<LedgerRun> {
  return ledgerBody(
    await post(API_URLS.dataHubLedgerKill(runId), { reason: reason ?? null })
  ) as LedgerRun;
}

/**
 * Persist one wizard step: save that step's decisions, then close it on the
 * ledger. Failures are swallowed so a ledger outage cannot freeze the console.
 */
async function persistLedgerStep(
  runId: string | null | undefined,
  step: string,
  decisions: Record<string, unknown> = {},
  close?: {
    outcome_counts?: Record<string, unknown>;
    artifact_evidence?: Record<string, unknown>;
  }
): Promise<void> {
  if (!runId) return;
  if (Object.keys(decisions).length) {
    try {
      await saveLedgerDecisions(runId, decisions);
    } catch {
      /* best-effort */
    }
  }
  try {
    await beginLedgerStep(runId, step);
  } catch {
    /* already running or already succeeded */
  }
  try {
    await succeedLedgerStep(runId, step, close);
  } catch {
    /* already closed */
  }
}

/** Partial save of the current decisions, without advancing a step. */
async function saveLedgerProgress(
  runId: string | null | undefined,
  decisions: Record<string, unknown>
): Promise<void> {
  if (!runId || !Object.keys(decisions).length) return;
  try {
    await saveLedgerDecisions(runId, decisions);
  } catch {
    /* best-effort */
  }
}

async function fetchRuns(): Promise<PipelineRun[]> {
  // Scope to the active pill — same contract as Admin Metrics Config. Showing
  // every dataset here made Production look like it contained Synthetic runs.
  const res = await get(
    API_URLS.dataHubRuns({ limit: 50, dataset: getActiveDataset() })
  );
  const content = unwrap<{ runs?: any[] }>(res, { runs: [] });
  const runs = Array.isArray(content?.runs) ? content.runs : [];
  // Newest first from the API; number them descending so #1 is the oldest.
  return runs.map((r, i) => toRun(r, runs.length - i));
}

async function fetchRun(runId: string): Promise<PipelineRun | null> {
  const res = await get(API_URLS.dataHubRun(runId));
  const content = unwrap<any>(res, null);
  return content ? toRun(content) : null;
}

/** All header counts, straight from the database. */
async function fetchStats(): Promise<DataHubStats> {
  const res = await get(API_URLS.dataHubStats());
  const c = unwrap<any>(res, {}) || {};
  const num = (v: any): number | null => (typeof v === "number" ? v : null);
  return {
    staging: num(c.staging),
    timeseries: num(c.timeseries),
    benchmarks: num(c.benchmarks),
    summaries: num(c.summaries),
    runs: num(c.runs),
  };
}

/* ---------------- console read surfaces (Phases 3–6) ---------------- */

/**
 * Fallback only.
 *
 * The backend now computes lifecycle via `core/lifecycle.py`, which is asserted
 * against the reference. This approximation is kept for responses recorded
 * before that, and is deliberately not the primary path: it got two cases wrong
 * — rejection signalled through `pipeline_status`, and a promoted bucket
 * leaking a production state into the Staging pane.
 */
function deriveLifecycle(bucket: string | null | undefined) {
  const rejected = bucket === "rejected";
  const state =
    bucket === "promoted"
      ? "production.promoted"
      : bucket === "fixed"
        ? "staging.fixed"
        : bucket === "deduped"
          ? "staging.deduped"
          : bucket === "ignored"
            ? "staging.ignored"
            : "staging.open";
  return {
    lifecycle_status: (rejected
      ? "rejected"
      : "accepted") as StagingRow["lifecycle_status"],
    stage_state: state as StagingRow["stage_state"],
  };
}

async function fetchStagingRows(): Promise<StagingRow[]> {
  const res = await get(API_URLS.dataHubStagingRows());
  const rows = unwrap<any>(res, {})?.rows ?? [];
  return rows.map((r: any) => ({
    id: String(r.id),
    account_id: r.account_id ?? null,
    account_name: r.account_name ?? null,
    source_account_id: r.source_account_id ?? null,
    source_account_name: r.source_account_name ?? null,
    metric_id: r.metric_id ?? null,
    metric_name: r.metric_name ?? null,
    category: r.category ?? null,
    measurement_date: r.measurement_date ?? null,
    value: r.value ?? null,
    // Served by the backend now, computed by `core/lifecycle.py` against the
    // reference. `deriveLifecycle` remains only as a fallback for a response
    // that predates it.
    lifecycle_status:
      r.lifecycle_status ?? deriveLifecycle(r.qc?.bucket).lifecycle_status,
    stage_state: r.stage_state ?? deriveLifecycle(r.qc?.bucket).stage_state,
    pipeline_status: r.pipeline_status ?? null,
    qc: r.qc ?? null,
    created_at: r.created_at ?? null,
  }));
}

async function fetchProductionRows(): Promise<ProductionRow[]> {
  const res = await get(API_URLS.dataHubProductionRows());
  const rows = unwrap<any>(res, {})?.rows ?? [];
  return rows.map((r: any) => ({
    id: String(r.id),
    measurement_date: r.measurement_date ?? "",
    account_name: r.account_name ?? "—",
    metric_name: r.metric_name ?? r.metric_id,
    metric_id: r.metric_id,
    category: r.category ?? "Uncategorized",
    value: Number(r.value ?? 0),
    normalized_value:
      r.normalized_value == null ? null : Number(r.normalized_value),
  }));
}

/** Which rung of the benchmark ladder a stored `benchmark_source` came from. */
const TIER_BY_SOURCE: Record<string, 0 | 1 | 2> = {
  org_calculated: 2,
  industry: 1,
  global_default: 1,
  seeded_rubric: 0,
};

/**
 * Fallback only, and the reason it is a fallback: this list is the frontend's
 * own copy of a decision that now lives in the rubric as
 * `normalization_direction`. It is consulted when a metric has no declared
 * direction — an unseeded catalog — and should shrink to nothing as orgs are
 * seeded, rather than being extended.
 */
const LOWER_IS_BETTER = new Set([
  "cac",
  "churn_rate_arr",
  "ttfv",
  "customer_response_time_avg",
  "ticket_resolution_time",
  "escalations_trend_30d",
  "escalations_count_30d",
  "open_tickets",
  "ticket_volume",
  "customer_risk_score",
]);

async function fetchBenchmarks(): Promise<BenchmarkMetric[]> {
  const res = await get(API_URLS.dataHubBenchmarks());
  const rows = unwrap<any>(res, {})?.benchmarks ?? [];
  const num = (v: any) => (v == null ? null : Number(v));

  // Nine benchmark_type rows share one distribution envelope. Collapse to one
  // card per metric; p10 lives on the percentile_10 row's benchmark_value.
  const byMetric = new Map<string, any>();
  for (const r of rows) {
    const id = String(r.metric_id ?? "");
    if (!id) continue;
    const existing = byMetric.get(id);
    if (!existing) {
      byMetric.set(id, { ...r });
    }
    const target = byMetric.get(id);
    if (r.p10 != null && target.p10 == null) target.p10 = r.p10;
    if (r.benchmark_type === "percentile_10" && r.benchmark_value != null) {
      target.p10 = r.benchmark_value;
    }
  }

  return Array.from(byMetric.values()).map((r: any) => ({
    metric_id: r.metric_id,
    metric_name: r.metric_name ?? r.metric_id,
    category: r.category ?? "Uncategorized",
    uom: r.uom ?? null,
    higher_is_better: r.normalization_direction
      ? r.normalization_direction !== "lower_better"
      : !LOWER_IS_BETTER.has(r.metric_id),
    min: num(r.min_value) ?? 0,
    p10: num(r.p10),
    p25: num(r.p25) ?? 0,
    p50: num(r.p50) ?? 0,
    p75: num(r.p75) ?? 0,
    p90: num(r.p90) ?? 0,
    max: num(r.max_value) ?? 0,
    mean: num(r.mean_value) ?? 0,
    stddev: num(r.std_dev) ?? 0,
    tier: TIER_BY_SOURCE[r.benchmark_source] ?? null,
    source: r.benchmark_source ?? r.benchmark_type ?? "org_calculated",
    // The row says how far to trust it. Falling back to the quality score keeps
    // rows written before the ladder existed rendering sensibly.
    confidence:
      r.confidence_tier ??
      ((num(r.confidence_score) ?? 0) >= 80
        ? "high"
        : (num(r.confidence_score) ?? 0) >= 50
          ? "medium"
          : "low"),
    dqs: num(r.data_quality_score) ?? 0,
    n: Number(r.unique_accounts_count ?? r.data_points_count ?? 0),
  }));
}

async function fetchMatrix(
  kind: "normalized" | "summarized",
  query: MatrixQuery = {}
): Promise<Matrix> {
  const res = await get(
    API_URLS.dataHubMatrix({
      kind,
      axis: query.axis ?? (kind === "summarized" ? "period" : "date"),
      period_type:
        kind === "summarized" ? (query.period_type ?? "monthly") : undefined,
    })
  );
  const c = unwrap<any>(res, {}) || {};
  return {
    metrics: (c.metrics ?? []).map((m: any) => ({
      metric_id: m.metric_id,
      metric_name: m.metric_name ?? m.metric_id,
      category: m.category ?? "Uncategorized",
      uom: m.uom ?? null,
    })),
    rows: (c.rows ?? []).map((r: any) => ({
      key: r.key,
      label: r.label,
      cells: r.cells ?? {},
    })),
  };
}

const fetchNormalizedMatrix = (query: MatrixQuery = {}) =>
  fetchMatrix("normalized", query);
const fetchSummarizedMatrix = (query: MatrixQuery = {}) =>
  fetchMatrix("summarized", query);

async function fetchCategoryScores(): Promise<CategoryScores> {
  const res = await post(
    API_URLS.dataHubCategoryScores({ persist: false }),
    {}
  );
  const c = unwrap<any>(res, {}) || {};
  return {
    saved_at: null,
    category_rows: (c.categories ?? []).length,
    overall_rows: c.accounts_scored ?? 0,
    composite: c.composite ?? null,
    categories: (c.categories ?? []).map((x: any) => ({
      category_key: x.category_key,
      label: x.category_key,
      score: x.score ?? null,
      max: 100,
    })),
    per_kpi: (c.kpis ?? []).map((k: any) => ({
      metric_id: k.metric_id,
      metric_name: k.metric_name ?? k.metric_id,
      category_key: k.category,
      effective_share: Number(((k.effective_share ?? 0) * 100).toFixed(1)),
      n: k.n ?? 0,
      score: k.score ?? 0,
    })),
    trend: (c.trend ?? []).map((p: any) => ({
      period_start: String(p.period_start ?? p.period ?? ""),
      composite: p.composite ?? null,
    })),
    // Only render the drift banner if the backend actually reports drift.
    label_drift: null,
  };
}

/** Shape a raw `POST /metrics/ro/compute?persist=false` body for the tab. */
function toRoScores(content: any): RoScores {
  const c = content || {};
  const dim = (d: any) => ({
    key: d.key,
    label: d.label ?? d.key,
    share: Number(((d.effective_share ?? 0) * 100).toFixed(1)),
    n: d.n ?? 0,
    score: d.score ?? 0,
  });
  return {
    rcs: c.rcs ?? 0,
    ocs: c.ocs ?? 0,
    rcs_band: c.rcs_band ?? "—",
    ocs_band: c.ocs_band ?? "—",
    risk_dimensions: (c.risk_dimensions ?? []).map(dim),
    opportunity_dimensions: (c.opportunity_dimensions ?? []).map(dim),
    accounts: (c.accounts ?? []).map((a: any) => ({
      account_id: a.account_id,
      account_name: a.account_name ?? a.account_id,
      rcs: a.rcs ?? 0,
      ocs: a.ocs ?? 0,
      quadrant: a.quadrant ?? "Q3",
    })),
    // Live now that the server computes them; these rendered empty tables
    // while `ro-scores/{trend,deltas}.ts` were unported.
    trend: (c.trend ?? []).map((p: any) => ({
      period_start: p.period_start,
      rcs: p.rcs ?? null,
      ocs: p.ocs ?? null,
    })),
    deltas: (c.deltas ?? []).map((d: any) => ({
      label: d.period_start,
      d_rcs: d.d_rcs ?? null,
      d_ocs: d.d_ocs ?? null,
      pct_rcs: d.pct_rcs ?? null,
      pct_ocs: d.pct_ocs ?? null,
    })),
    dimension_deltas: (c.dimension_deltas?.risk ?? []).map((r: any) => ({
      key: r.key,
      label: r.label,
      cells: (r.cells ?? []).map((cell: any) => ({
        period_start: cell.period_start,
        delta: cell.delta ?? null,
      })),
    })),
    top_movers: [
      ...(c.top_movers?.improved ?? []),
      ...(c.top_movers?.declined ?? []),
    ].map((m: any) => ({
      account_name: m.account_name,
      metric: "RCS",
      delta: m.delta ?? 0,
    })),
  };
}

/* ---------------- QC report ---------------- */

/**
 * ExecBridge ties QC verdicts to a `qc_run_id` on the staging rows, not to a
 * pipeline run, so the report describes the most recent QC pass rather than
 * being selected by pipeline run id.
 */
async function fetchQcReport(
  scope: "file" | "production",
  runId?: string
): Promise<QcReport | null> {
  // Scoped to the selected run's batch. Staging is never cleared, so an
  // unscoped summary reports every import at once while the picker above it
  // claims to be showing one.
  const res = await get(API_URLS.dataHubQcSummary({ run_id: runId }));
  const c = unwrap<any>(res, null);
  if (!c) return null;
  const b = c.buckets ?? {};
  return {
    run_id: c.qc_run_id ?? "—",
    generated_at: c.generated_at ?? new Date().toISOString(),
    scope,
    rows_scanned: c.rows ?? 0,
    rows_auto_fixed: c.rows_auto_fixed ?? 0,
    rows_open: b.open ?? 0,
    rows_rejected: b.rejected ?? 0,
    by_probe: (c.by_probe ?? []).map((p: any) => ({
      probe_id: p.probe_id,
      label: p.label ?? p.probe_id,
      group: p.group ?? "value_conformance",
      severity: p.severity ?? "warn",
      count: p.count ?? 0,
    })),
  };
}

async function fetchQcProbes(): Promise<QcProbe[]> {
  const res = await get(API_URLS.dataHubQcProbes());
  return unwrap<any>(res, {})?.probes ?? [];
}

/**
 * Phases the wizard can actually execute, and the endpoint that does the work.
 *
 * `import` and `commit` are absent deliberately: both need an uploaded file, so
 * they are driven from Upload Data rather than from here. The orchestrator's
 * prerequisite rule only blocks on phases that were *requested*, so omitting
 * them lets cleanse and the Phase-2 phases run against existing data.
 */
/**
 * Import + Commit need the uploaded file, so they are built per-run rather than
 * declared statically. Import stages the CSV and stashes the batch id; Commit
 * resolves accounts for that batch and transfers it to production.
 */
function fileRunners(file: File, ctx: { batchId?: string | number }) {
  return {
    import: async () => {
      const res = await uploadFile(API_URLS.metricsImportCsv(), file);
      const c = (res as any)?.content ?? res ?? {};
      ctx.batchId = c.batch_id ?? c.batchId;
      if (ctx.batchId == null) throw new Error("import returned no batch id");

      // An import that stages nothing is a failure, not a success. Without this
      // the run reports "succeeded" while the database is untouched.
      const sum = c.summary ?? {};
      const imported = Number(sum.imported_rows ?? sum.inserted_rows ?? 0);
      const errored = Number(sum.error_rows ?? 0);
      const total = Number(sum.total_rows ?? 0);

      // The importer de-duplicates within the file on the natural key in
      // `_staging_natural_key`: account_id if present, else source_account_id,
      // else source_account_name — plus metric and date. Last row wins.
      //
      // Blaming one column here would be a guess: a constant placeholder in
      // ANY part of that key collapses distinct rows. Report the fact and name
      // the whole key rather than pointing at a likely suspect.
      const deduped = Number(sum.duplicate_rows_in_file ?? 0);
      if (imported > 0 && deduped > 0) {
        // Name the collisions rather than only counting them: "2 rows merged"
        // cannot be told apart from a mis-mapped column that collapsed the
        // whole file, which is exactly the mistake worth catching here.
        const examples = (sum.duplicate_examples ?? [])
          .slice(0, 3)
          .map(
            (e: any) =>
              `${e.account} · ${e.metric} · ${e.date}` +
              (e.replaced_row && e.kept_row
                ? ` (row ${e.replaced_row} replaced by row ${e.kept_row})`
                : "")
          );
        (res as any).__partial =
          `staged ${imported} of ${total} rows — ${deduped} shared an ` +
          "account + metric + date with another row and were merged, keeping " +
          "the last." +
          (examples.length
            ? ` Merged: ${examples.join("; ")}${
                deduped > examples.length
                  ? `; and ${deduped - examples.length} more`
                  : ""
              }.`
            : "") +
          " If that count looks too high, check the Columns step: a date or " +
          "account column holding the same value on every row will make " +
          "genuinely distinct rows collide.";
      }

      // A partial import is not a clean success: reporting "25 rows" while 74
      // silently failed is how an import looks fine and changes almost nothing.
      if (imported > 0 && errored > 0) {
        const detail = (sum.validation_errors ?? [])
          .slice(0, 3)
          .map((e: any) =>
            typeof e === "string"
              ? e
              : (e?.message ?? e?.error ?? JSON.stringify(e))
          )
          .join("; ");
        (res as any).__partial =
          `staged ${imported} of ${total} rows — ${errored} failed validation` +
          (detail ? `. First issues: ${detail}` : "");
      }

      if (imported === 0) {
        const reasons: string[] = [];
        if (total === 0)
          reasons.push("the sheet had no data rows below the header");
        if (errored > 0)
          reasons.push(`${errored} of ${total} rows failed validation`);
        const detail = (sum.validation_errors ?? [])
          .slice(0, 3)
          .map((e: any) =>
            typeof e === "string"
              ? e
              : (e?.message ?? e?.error ?? JSON.stringify(e))
          )
          .join("; ");
        throw new Error(
          `imported 0 rows${reasons.length ? ` — ${reasons.join(", ")}` : ""}` +
            (detail ? `. First issues: ${detail}` : "")
        );
      }
      return res;
    },
    commit: async () => {
      if (ctx.batchId == null) throw new Error("no import batch to commit");
      // Resolve accounts / validate the batch, then move it to production.
      await post(API_URLS.metricsStagingProcess(ctx.batchId), {});
      // clear_staging: false — the Data Hub treats staging as a reviewable
      // record, not a scratch buffer. Rows stay put so the Staging tab and the
      // heal pane can show what landed and what still needs fixing. Transfer is
      // idempotent (ON CONFLICT DO UPDATE), so re-running is safe.
      return post(
        API_URLS.metricsStagingTransfer({
          batch_id: ctx.batchId,
          clear_staging: false,
          // Duplicates the user has not adjudicated stay in staging. Without
          // this the transfer's DISTINCT ON silently keeps whichever row
          // imported last, which is the decision Heal exists to take away
          // from it.
          exclude_unadjudicated: true,
        }),
        {}
      );
    },
  };
}

/**
 * Benchmark phase must both:
 * 1) write `organization_benchmarks` (calculate)
 * 2) stamp `timeseries.metadata.organization_benchmarks` (propagate)
 * Normalize reads metadata only — calculate alone leaves it on direct_mapping.
 */
async function runBenchmarkPhase(): Promise<any> {
  const calcRes = await post(
    API_URLS.metricsBenchmarksCalculate({
      time_window_months: 6,
      min_data_points: 3,
      // XF Test Org parity: publish with ≥2 accounts (EB used to require 3).
      min_unique_accounts: 2,
    }),
    {}
  );
  const propRes = await post(API_URLS.metricsBenchmarksPropagate(), {});
  const calc = unwrap<any>(calcRes, {});
  const prop = unwrap<any>(propRes, {});
  return {
    content: {
      created_metrics: calc.created_metrics ?? 0,
      demoted_metrics: calc.demoted_metrics ?? 0,
      skipped_metrics: calc.skipped_metrics ?? 0,
      updated_metrics: prop.updated_metrics ?? 0,
      updated_rows: prop.updated_rows ?? 0,
      // Prefer propagated row count for the run console badge.
      rows_written: prop.updated_rows ?? calc.created_metrics ?? 0,
      calculate: calc,
      propagate: prop,
    },
  };
}

const PHASE_RUNNERS: Partial<Record<PhaseKey, () => Promise<any>>> = {
  cleanse: () => post(API_URLS.dataHubQcRun(), {}),
  benchmark: () => runBenchmarkPhase(),
  normalize: () => post(API_URLS.metricsNormalize(), {}),
  summarize: () => post(API_URLS.dataHubRollup(), {}),
  kpi_scores: () => post(API_URLS.dataHubCategoryScores({ persist: true }), {}),
  ...(RO_SCORES_ENABLED
    ? {
        ro_scores: () => post(API_URLS.dataHubRoCompute({ persist: true }), {}),
      }
    : {}),
};

/** Accounts in this organization, for the wizard's Accounts step. */
async function fetchAccountOptions(): Promise<
  { account_id: string; account_name: string }[]
> {
  const res = await get(API_URLS.dataHubAccounts());
  return unwrap<any>(res, {})?.accounts ?? [];
}

/** Metric catalogue + its distinct categories, for Metrics and Categories. */
async function fetchMetricDefinitions(): Promise<{
  metrics: {
    metric_id: string;
    metric_name: string;
    category: string;
    unit_of_measure: string;
    format?: string;
    scale?: string;
    /** Other ids for the same metric (the reference app's renamed ids). */
    aliases?: string[];
  }[];
  categories: string[];
  /** One entry per category, by key. What the custom-metric form offers. */
  categoryOptions: CategoryOption[];
}> {
  const res = await get(API_URLS.dataHubMetricDefinitions());
  const c = unwrap<any>(res, {}) ?? {};
  return {
    metrics: c.metrics ?? [],
    categories: c.categories ?? [],
    categoryOptions: c.category_options ?? [],
  };
}

/** Every phase a run can record, in execution order. */
const ALL_PHASES: PhaseKey[] = [
  "import",
  "cleanse",
  "commit",
  "benchmark",
  "normalize",
  "summarize",
  "kpi_scores",
  ...(RO_SCORES_ENABLED ? (["ro_scores"] as PhaseKey[]) : []),
];

/** The QC review queue, for the Heal step. */
async function fetchQcIssues(
  // The server's page cap. At 200 a 459-row file silently showed less than
  // half its flagged rows, and bulk actions then disagreed with the screen.
  limit = 500,
  batchId?: string | number | null
): Promise<any[]> {
  // Scoped to the run's batch: staging is deliberately never cleared, so an
  // unscoped queue shows rows from earlier runs as if they were this one's.
  const scope = batchId != null ? `&batch_id=${Number(batchId)}` : "";
  const res = await get(`${API_URLS.dataHubQcIssues()}?limit=${limit}${scope}`);
  const c = unwrap<any>(res, {}) ?? {};
  return c.issues ?? c.rows ?? [];
}

/**
 * Resolve one flagged row from the review queue.
 *
 * `edit` writes a corrected value and clears the verdict so the next QC pass
 * re-evaluates the row; `ignore` accepts it as it stands. Both mirror
 * `services/datahub/qc_service.py::resolve`.
 */
async function resolveQcRow(input: {
  row_id: string;
  action: "edit" | "ignore" | "accept" | "reject";
  value?: string;
  /** With `accept`: apply only this problem type's fixes. */
  probe_id?: string;
}): Promise<{ id: string; pipeline_status: string | null }> {
  const res = await post(API_URLS.dataHubQcResolve(), input);
  return unwrap<any>(res, {});
}

/**
 * The created row, or a thrown error carrying what the server said.
 *
 * `create_custom_metric_endpoint` reports a refusal as `status: "failure"`
 * inside an HTTP **200** — a duplicate id, a missing field, a category this org
 * does not have. `unwrap` reads `content` without looking at `status`, so the
 * refusal arrives as `{detail: "…"}` and every truthiness check on it passes.
 *
 * The absent key is what makes the difference detectable. Without this the
 * dialog treats a rejection as a success: it closes, reports the metric
 * created, and maps the file's rows onto `undefined` — so the rows import
 * attached to nothing and the user is never told.
 */
function created<T>(res: any, key: string, fallback: string): T {
  const body = unwrap<any>(res, {});
  if (!body?.[key]) {
    throw new Error(res?.message ?? body?.detail ?? fallback);
  }
  return body as T;
}

/** A server "failure" answer carries the reason; surface it as an Error. */
function orThrow<T>(res: any, fallback: string): T {
  const status = res?.response?.status;
  if (typeof status === "number" && status >= 400) {
    throw new Error(res?.response?.message ?? res?.content?.detail ?? fallback);
  }
  return unwrap<T>(res, {} as T);
}

async function fetchCustomRubric(): Promise<{
  metrics: CustomMetric[];
  categories: RubricCategory[];
}> {
  const res = await get(API_URLS.dataHubCustomRubric());
  const c = unwrap<any>(res, {}) ?? {};
  return { metrics: c.metrics ?? [], categories: c.categories ?? [] };
}

async function updateCustomMetric(
  metricId: string,
  changes: Partial<CustomMetric>
): Promise<void> {
  orThrow(
    await rawPut(API_URLS.dataHubCustomMetric(metricId), changes),
    "The metric was not updated."
  );
}

async function deleteCustomMetric(metricId: string): Promise<void> {
  orThrow(
    await rawDel(API_URLS.dataHubCustomMetric(metricId)),
    "The metric was not deleted."
  );
}

async function updateCustomCategory(
  key: string,
  changes: { label?: string; long_label?: string; description?: string }
): Promise<void> {
  orThrow(
    await rawPut(API_URLS.dataHubCustomCategory(key), changes),
    "The category was not updated."
  );
}

async function deleteCustomCategory(key: string): Promise<void> {
  orThrow(
    await rawDel(API_URLS.dataHubCustomCategory(key)),
    "The category was not deleted."
  );
}

/** Plan (apply=false) or apply a rubric import. Rows are keyed by header. */
async function importCustomRubric(
  rows: Record<string, string>[],
  apply: boolean
): Promise<{
  applied: boolean;
  summary: Record<string, number>;
  plan: RubricImportPlanEntry[];
}> {
  const res = await post(API_URLS.dataHubCustomRubricImport(), {
    rows,
    apply,
  });
  return orThrow(res, "The rubric could not be read.");
}

/**
 * Create a category for this organization only.
 *
 * The key is derived from the label server-side, so two spellings of the same
 * name cannot become two categories that score separately.
 */
async function createCustomCategory(input: {
  label: string;
  long_label?: string;
  description?: string;
  sort_order?: number;
}): Promise<{ category_key: string; label: string }> {
  const res = await post(API_URLS.dataHubCustomCategories(), input);
  return created(res, "category_key", "The category was not created.");
}

/**
 * Create a metric for this organization only.
 *
 * Every field is required by the server, because a custom metric has no master
 * row to inherit from — see `CUSTOM_METRIC_REQUIRED` in services/kpi_catalog.py.
 */
async function createCustomMetric(input: {
  metric_id: string;
  metric_name: string;
  category_key: string;
  unit_of_measure: string;
  format: string;
  scale: string;
  normalization_type: string;
  normalization_direction: "higher_better" | "lower_better" | string;
  subcategory?: string;
  description?: string;
  business_impact?: string;
  formula?: string;
  source_system?: string;
  kpi_status?: string;
}): Promise<{ metric_id: string; metric_name: string }> {
  const res = await post(API_URLS.dataHubCustomMetrics(), input);
  return created(res, "metric_id", "The metric was not created.");
}

/**
 * Duplicate rows whose values disagree, grouped by dedupe key.
 *
 * Kept apart from `fetchQcIssues` on purpose: these rows have no finding to
 * heal and no proposal to accept. The only resolution is a person choosing
 * which number is true.
 */
async function fetchQcConflicts(
  batchId?: string | number | null
): Promise<QcConflictGroup[]> {
  const res = await get(
    API_URLS.dataHubQcConflicts(
      batchId != null ? { batch_id: Number(batchId) } : {}
    )
  );
  const c = unwrap<any>(res, {}) ?? {};
  return c.conflicts ?? [];
}

/** Keep one row from a conflict group; the rest are marked duplicate. */
async function resolveQcConflict(input: {
  key: string;
  winner_row_id: string;
}): Promise<{ key: string; winner_row_id: string; discarded: number }> {
  const res = await post(API_URLS.dataHubQcConflictResolve(), input);
  return unwrap<any>(res, {});
}

/** Inputs for the QC Report — File. Density itself is computed client-side. */
async function fetchQcFileReport(params?: {
  run_id?: string;
  batch_id?: number;
}): Promise<{
  rubric: { metric_id: string; metric_name: string; category: string }[];
  rows: {
    account_name: string | null;
    metric_id: string | null;
    category: string | null;
    measurement_date: string | null;
  }[];
  decisions: Record<string, Record<string, number>>;
}> {
  const res = await get(API_URLS.dataHubQcFileReport(params));
  const c = unwrap<any>(res, {}) ?? {};
  return {
    rubric: c.rubric ?? [],
    rows: c.rows ?? [],
    decisions: c.decisions ?? {},
  };
}

function healParams(
  batchId: string | number | null | undefined,
  scope: HealScope
) {
  return {
    ...(batchId != null ? { batch_id: Number(batchId) } : {}),
    ...(scope.probeId ? { probe_id: scope.probeId } : {}),
    ...(scope.metricId ? { metric_id: scope.metricId } : {}),
  };
}

/**
 * Apply every proposed fix in the queue at once.
 *
 * Only rows the engine could answer change; a finding with no proposal is left
 * for a human rather than resolved by a guess.
 */
async function acceptAllQcFixes(
  batchId?: string | number | null,
  scope: HealScope = {}
): Promise<{
  rows_scanned: number;
  rows_changed: number;
  fixes_applied: number;
}> {
  const res = await post(
    API_URLS.dataHubQcAcceptAll(healParams(batchId, scope)),
    {}
  );
  return unwrap<any>(res, {});
}

/** Mark every queued row in scope as never to be promoted. */
async function rejectAllQcRows(
  batchId: string | number | null | undefined,
  scope: HealScope = {}
): Promise<{ rows_scanned: number; rows_rejected: number }> {
  const res = await post(
    API_URLS.dataHubQcRejectAll(healParams(batchId, scope)),
    {}
  );
  return unwrap<any>(res, {});
}

/** Read-only audit of this organization's pipeline data. */
async function runQaAudit(): Promise<QaReport> {
  const res = await post(API_URLS.dataHubQa(), {});
  return unwrap<any>(res, { summary: {}, checks: [] });
}

/**
 * What the QC pass actually found, scoped to this run's batch.
 *
 * The pass already returns all of this; the wizard used to discard it and jump
 * straight to Heal, so a user was asked to fix rows without being told what was
 * wrong with them or how many.
 */
async function fetchQcRunSummary(
  batchId?: string | number | null
): Promise<QcRunSummary> {
  const res = await get(
    API_URLS.dataHubQcSummary(
      batchId != null ? { batch_id: Number(batchId) } : {}
    )
  );
  const c = unwrap<any>(res, {}) ?? {};
  const b = c.buckets ?? {};
  return {
    rows_scanned: c.rows ?? c.rows_scanned ?? 0,
    buckets: {
      clean: b.clean ?? 0,
      fixed: b.fixed ?? 0,
      open: b.open ?? 0,
      rejected: b.rejected ?? 0,
      ignored: b.ignored ?? 0,
      duplicate: b.duplicate ?? 0,
      conflict: b.conflict ?? 0,
      user_rejected: b.user_rejected ?? 0,
    },
    unscanned: c.unscanned ?? 0,
    by_probe: c.by_probe ?? [],
    dedupe: c.dedupe ?? { auto_deduped: 0, conflicts: 0 },
  };
}

/** Download rejected rows as CSV, for offline correction and re-import. */
function rejectedRowsCsvUrl(batchId?: string | number | null): string {
  return API_URLS.dataHubRejectedRowsCsv(
    batchId != null ? { batch_id: Number(batchId) } : {}
  );
}

/**
 * Fetch a CSV through the shared client and hand it to the browser as a file.
 *
 * Never `window.open` an API URL: a top-level navigation carries none of the
 * headers this app authenticates with (Authorization, X-Tenant-Domain, the
 * dataset), so the server answers "Not authenticated" and the user gets a JSON
 * error page instead of a file.
 */
async function downloadCsv(url: string, filename: string): Promise<void> {
  const blob: Blob = await get(url, { responseType: "blob" });
  const href = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoked on the next tick so the click has taken the reference first.
    setTimeout(() => URL.revokeObjectURL(href), 0);
  }
}

function downloadRejectedRowsCsv(
  batchId?: string | number | null
): Promise<void> {
  const suffix = batchId != null ? `-batch-${batchId}` : "";
  return downloadCsv(rejectedRowsCsvUrl(batchId), `flagged-rows${suffix}.csv`);
}

/** The org's import template, or the shipped format when none is configured. */
async function fetchImportTemplate(): Promise<ImportTemplate | null> {
  try {
    const res = await get(API_URLS.dataHubImportTemplate());
    return (unwrap<any>(res, null) as ImportTemplate) ?? null;
  } catch {
    // A template is an enhancement, not a prerequisite: without it the Columns
    // step uses the built-in aliases exactly as it did before.
    return null;
  }
}

/* ---------------- Run sessions ----------------
 *
 * The stepped wizard runs one phase at a time with a human in between. The
 * session handle (`data-hub-library/data/runSession.ts`) holds the run id and
 * the batch id between steps and dispatches the thunks below; each thunk
 * rebuilds the runners from what it is given, so the batch id stamped by
 * Import is what Commit and the batch-scoped QC pass see next.
 */

type SessionCtx = { batchId?: string | number };

function sessionRunners(
  file: File | null,
  ctx: SessionCtx
): Partial<Record<PhaseKey, () => Promise<any>>> {
  const withFile = file ? fileRunners(file, ctx) : null;
  return {
    ...PHASE_RUNNERS,
    ...(withFile ? { import: withFile.import, commit: withFile.commit } : {}),
    // Scope QC to this run's batch. Without it every pass re-processes every
    // row left in staging by earlier runs — slower each time, and
    // DUPE_IN_STAGING starts flagging cross-run duplicates that are not real
    // problems. Staging is deliberately not cleared, so this matters.
    cleanse: () =>
      post(API_URLS.dataHubQcRun(), {
        ...(ctx.batchId != null ? { batch_id: Number(ctx.batchId) } : {}),
      }),
  };
}

async function runSessionPhase(
  runId: string,
  phase: PhaseKey,
  file: File | null,
  ctx: SessionCtx,
  plan?: Record<string, boolean> | null
): Promise<RunPhaseOutcome> {
  // Ask the server to open the phase first: it owns the prerequisite and
  // source-guard rules, so the client never runs work the record calls
  // skipped. Duplicating those rules here would let them drift.
  try {
    const openedPhase = await post(API_URLS.dataHubRunPhase(runId, phase), {
      status: "running",
      count: null,
      error: null,
    });
    const recorded = (unwrap<any>(openedPhase, {})?.phases ?? []).find(
      (x: any) => x.phase === phase
    );
    if (recorded?.status === "skipped") {
      return { phase, status: "skipped", error: recorded.reason };
    }
  } catch {
    // If the pre-check itself fails, still attempt the phase.
  }

  // A probe plan replaces the default runner for QC, and only for QC.
  const runner =
    phase === "cleanse" && plan
      ? () => post(API_URLS.dataHubQcRun(), { plan })
      : sessionRunners(file, ctx)[phase];
  if (!runner) return { phase, status: "skipped" };

  try {
    const result = await runner();
    const partial = (result as any)?.__partial as string | undefined;
    // Stamp the batch onto the import phase so a run can be traced back to
    // the rows it staged. The QC Report needs this to describe one import
    // rather than everything sitting in staging.
    const extras =
      phase === "import" && ctx.batchId != null
        ? { batch_id: Number(ctx.batchId) }
        : null;
    const outcome: RunPhaseOutcome = {
      phase,
      status: "success",
      count: measureFor(phase, result).count,
      measure: measureFor(phase, result),
      ...(partial ? { error: partial } : {}),
    };
    await post(API_URLS.dataHubRunPhase(runId, phase), {
      status: "success",
      count: outcome.count ?? null,
      error: null,
      extras,
    });
    return outcome;
  } catch (err: any) {
    const message = describeError(err);
    await post(API_URLS.dataHubRunPhase(runId, phase), {
      status: "failed",
      count: null,
      error: message,
    });
    return { phase, status: "failed", error: message };
  }
}

/** Open a `pipeline_runs` row for the stepped wizard; resolves to its id. */
export const openDataHubRun = createAsyncThunk<string, { source?: string }>(
  "dataHubConsole/openRun",
  async ({ source }) => {
    // `phases` is required by PipelineRunOpenRequest and is the record of what
    // this run intends to do. The stepped wizard runs all of them, one at a
    // time with a human in between, so every phase is requested up front.
    const phases = Object.fromEntries(ALL_PHASES.map(p => [p, true])) as Record<
      string,
      boolean
    >;
    const opened = await post(API_URLS.dataHubRuns(), {
      phases,
      source: source ?? "Data Hub console",
      // Explicit body + header: open_run prefers body.dataset, then the
      // header. Pinning both keeps a mid-run pill flip from tagging the wrong
      // lane.
      dataset: getActiveDataset(),
    });
    const runId = String(unwrap<any>(opened, {})?.id ?? "");
    if (!runId) throw new Error("could not open a pipeline run");
    return runId;
  }
);

/**
 * Run one phase of an open run. `plan` reaches the QC pass only. Resolves to
 * the outcome plus the batch id, which Import sets and later phases need.
 */
export const runDataHubRunPhase = createAsyncThunk<
  { outcome: RunPhaseOutcome; batchId: string | number | undefined },
  {
    runId: string;
    phase: PhaseKey;
    file: File | null;
    batchId?: string | number | null;
    plan?: Record<string, boolean> | null;
  }
>("dataHubConsole/runPhase", async ({ runId, phase, file, batchId, plan }) => {
  const ctx: SessionCtx = {};
  if (batchId != null) ctx.batchId = batchId;
  const outcome = await runSessionPhase(runId, phase, file, ctx, plan);
  return { outcome, batchId: ctx.batchId };
});

/**
 * Record a step that made a decision rather than executing work — Columns,
 * Accounts, Metrics, Categories. Resolves to null on success, or the reason it
 * could not be recorded.
 *
 * Bookkeeping must never block the run — but it must not be silent either.
 * Swallowing the error here meant every decision step was rejected for a
 * whole session while the run reported success and the audit trail was
 * simply absent.
 */
export const recordDataHubRunStep = createAsyncThunk<
  string | null,
  {
    runId: string;
    step: string;
    detail: { count?: number | null; extras?: Record<string, unknown> };
  }
>("dataHubConsole/recordStep", async ({ runId, step, detail }) => {
  try {
    const res = await post(API_URLS.dataHubRunPhase(runId, step), {
      status: "success",
      count: detail.count ?? null,
      error: null,
      extras: detail.extras ?? null,
    });
    // `response.status` duplicates the HTTP status CODE (200), not a word.
    // Comparing it to "success" made every successful step report itself as
    // a failure, so the console warned that nothing had been recorded while
    // the database held all of it.
    const code = Number((res as any)?.response?.status ?? 200);
    if (code >= 400) {
      return `${step}: ${(res as any)?.response?.message ?? code}`;
    }
    return null;
  } catch (err: any) {
    return `${step}: ${describeError(err)}`;
  }
});

/** The server rolls the status up from the recorded phases; no argument. */
export const closeDataHubRun = createAsyncThunk<void, string>(
  "dataHubConsole/closeRun",
  async runId => {
    await post(API_URLS.dataHubRunClose(runId), {});
  }
);

/* ---------------- Thunks ---------------- */

// Stored files
/** Resolves to a File — see NON_SERIALIZABLE_DATA_HUB_ACTIONS. */
export const fetchDataHubStoredFile = createAsyncThunk<File, StoredFile>(
  "dataHubConsole/fetchStoredFile",
  stored => fetchStoredFileAsFile(stored)
);
export const uploadDataHubStoredFile = createAsyncThunk<
  StoredFile | null,
  File
>("dataHubConsole/uploadStoredFile", file => uploadDataHubFile(file));

// Ledger
export const listDataHubLedgerRuns = createAsyncThunk<
  LedgerRun[],
  { dataset?: string; limit?: number } | undefined
>("dataHubConsole/listLedgerRuns", params => listLedgerRuns(params));
export const fetchDataHubLedgerRun = createAsyncThunk<LedgerRun, string>(
  "dataHubConsole/fetchLedgerRun",
  runId => fetchLedgerRun(runId)
);
export const openDataHubLedgerRun = createAsyncThunk<
  LedgerRun,
  Parameters<typeof openLedgerRun>[0]
>("dataHubConsole/openLedgerRun", input => openLedgerRun(input));
export const killDataHubLedgerRun = createAsyncThunk<
  LedgerRun,
  { runId: string; reason?: string }
>("dataHubConsole/killLedgerRun", ({ runId, reason }) =>
  killLedgerRun(runId, reason)
);
/** Best-effort: never rejects, so a ledger outage cannot freeze the console. */
export const persistDataHubLedgerStep = createAsyncThunk<
  void,
  {
    runId: string | null | undefined;
    step: string;
    decisions?: Record<string, unknown>;
    close?: Parameters<typeof persistLedgerStep>[3];
  }
>("dataHubConsole/persistLedgerStep", ({ runId, step, decisions, close }) =>
  persistLedgerStep(runId, step, decisions, close)
);
/** Best-effort partial save; never rejects. */
export const saveDataHubLedgerProgress = createAsyncThunk<
  void,
  { runId: string | null | undefined; decisions: Record<string, unknown> }
>("dataHubConsole/saveLedgerProgress", ({ runId, decisions }) =>
  saveLedgerProgress(runId, decisions)
);

// Console read surfaces (the adapter's live half)
export const fetchDataHubStats = createAsyncThunk<DataHubStats, void>(
  "dataHubConsole/fetchStats",
  () => fetchStats()
);
export const fetchDataHubRuns = createAsyncThunk<PipelineRun[], void>(
  "dataHubConsole/fetchRuns",
  () => fetchRuns()
);
export const fetchDataHubRun = createAsyncThunk<PipelineRun | null, string>(
  "dataHubConsole/fetchRun",
  runId => fetchRun(runId)
);
export const fetchDataHubStagingRows = createAsyncThunk<StagingRow[], void>(
  "dataHubConsole/fetchStagingRows",
  () => fetchStagingRows()
);
export const fetchDataHubProductionRows = createAsyncThunk<
  ProductionRow[],
  void
>("dataHubConsole/fetchProductionRows", () => fetchProductionRows());
export const fetchDataHubBenchmarks = createAsyncThunk<BenchmarkMetric[], void>(
  "dataHubConsole/fetchBenchmarks",
  () => fetchBenchmarks()
);
export const fetchDataHubNormalizedMatrix = createAsyncThunk<
  Matrix,
  MatrixQuery | undefined
>("dataHubConsole/fetchNormalizedMatrix", query =>
  fetchNormalizedMatrix(query)
);
export const fetchDataHubSummarizedMatrix = createAsyncThunk<
  Matrix,
  MatrixQuery | undefined
>("dataHubConsole/fetchSummarizedMatrix", query =>
  fetchSummarizedMatrix(query)
);
export const fetchDataHubCategoryScores = createAsyncThunk<
  CategoryScores,
  void
>("dataHubConsole/fetchCategoryScores", () => fetchCategoryScores());
/** Reuses DataHubSlice's compute call (same endpoint and dataset header). */
export const fetchDataHubRoScores = createAsyncThunk<RoScores, void>(
  "dataHubConsole/fetchRoScores",
  async (_, { dispatch }) =>
    toRoScores(await dispatch(computeDataHubRoScores()).unwrap())
);
export const fetchDataHubQcReport = createAsyncThunk<
  QcReport | null,
  { scope: "file" | "production"; runId?: string }
>("dataHubConsole/fetchQcReport", ({ scope, runId }) =>
  fetchQcReport(scope, runId)
);
export const fetchDataHubQcProbes = createAsyncThunk<QcProbe[], void>(
  "dataHubConsole/fetchQcProbes",
  () => fetchQcProbes()
);

// Wizard reference data
export const fetchDataHubAccountOptions = createAsyncThunk<
  { account_id: string; account_name: string }[],
  void
>("dataHubConsole/fetchAccountOptions", () => fetchAccountOptions());
export const fetchDataHubMetricDefinitions = createAsyncThunk<
  Awaited<ReturnType<typeof fetchMetricDefinitions>>,
  void
>("dataHubConsole/fetchMetricDefinitions", () => fetchMetricDefinitions());
/** Never rejects: a missing template falls back to the built-in aliases. */
export const fetchDataHubImportTemplate = createAsyncThunk<
  ImportTemplate | null,
  void
>("dataHubConsole/fetchImportTemplate", () => fetchImportTemplate());

// QC / Heal
export const fetchDataHubQcIssues = createAsyncThunk<
  any[],
  { limit?: number; batchId?: string | number | null }
>("dataHubConsole/fetchQcIssues", ({ limit, batchId }) =>
  fetchQcIssues(limit, batchId)
);
export const resolveDataHubQcRow = createAsyncThunk<
  { id: string; pipeline_status: string | null },
  Parameters<typeof resolveQcRow>[0]
>("dataHubConsole/resolveQcRow", input => resolveQcRow(input));
export const fetchDataHubQcConflicts = createAsyncThunk<
  QcConflictGroup[],
  string | number | null | undefined
>("dataHubConsole/fetchQcConflicts", batchId => fetchQcConflicts(batchId));
export const resolveDataHubQcConflict = createAsyncThunk<
  { key: string; winner_row_id: string; discarded: number },
  { key: string; winner_row_id: string }
>("dataHubConsole/resolveQcConflict", input => resolveQcConflict(input));
export const fetchDataHubQcFileReport = createAsyncThunk<
  Awaited<ReturnType<typeof fetchQcFileReport>>,
  { run_id?: string; batch_id?: number } | undefined
>("dataHubConsole/fetchQcFileReport", params => fetchQcFileReport(params));
export const acceptAllDataHubQcFixes = createAsyncThunk<
  { rows_scanned: number; rows_changed: number; fixes_applied: number },
  { batchId?: string | number | null; scope?: HealScope }
>("dataHubConsole/acceptAllQcFixes", ({ batchId, scope }) =>
  acceptAllQcFixes(batchId, scope)
);
export const rejectAllDataHubQcRows = createAsyncThunk<
  { rows_scanned: number; rows_rejected: number },
  { batchId?: string | number | null; scope?: HealScope }
>("dataHubConsole/rejectAllQcRows", ({ batchId, scope }) =>
  rejectAllQcRows(batchId, scope)
);
export const fetchDataHubQcRunSummary = createAsyncThunk<
  QcRunSummary,
  string | number | null | undefined
>("dataHubConsole/fetchQcRunSummary", batchId => fetchQcRunSummary(batchId));
export const downloadDataHubRejectedRowsCsv = createAsyncThunk<
  void,
  string | number | null | undefined
>("dataHubConsole/downloadRejectedRowsCsv", batchId =>
  downloadRejectedRowsCsv(batchId)
);

// Custom rubric
export const fetchDataHubCustomRubric = createAsyncThunk<
  { metrics: CustomMetric[]; categories: RubricCategory[] },
  void
>("dataHubConsole/fetchCustomRubric", () => fetchCustomRubric());
export const updateDataHubCustomMetric = createAsyncThunk<
  void,
  { metricId: string; changes: Partial<CustomMetric> }
>("dataHubConsole/updateCustomMetric", ({ metricId, changes }) =>
  updateCustomMetric(metricId, changes)
);
export const deleteDataHubCustomMetric = createAsyncThunk<void, string>(
  "dataHubConsole/deleteCustomMetric",
  metricId => deleteCustomMetric(metricId)
);
export const updateDataHubCustomCategory = createAsyncThunk<
  void,
  { key: string; changes: Parameters<typeof updateCustomCategory>[1] }
>("dataHubConsole/updateCustomCategory", ({ key, changes }) =>
  updateCustomCategory(key, changes)
);
export const deleteDataHubCustomCategory = createAsyncThunk<void, string>(
  "dataHubConsole/deleteCustomCategory",
  key => deleteCustomCategory(key)
);
export const importDataHubCustomRubric = createAsyncThunk<
  {
    applied: boolean;
    summary: Record<string, number>;
    plan: RubricImportPlanEntry[];
  },
  { rows: Record<string, string>[]; apply: boolean }
>("dataHubConsole/importCustomRubric", ({ rows, apply }) =>
  importCustomRubric(rows, apply)
);
export const createDataHubCustomCategory = createAsyncThunk<
  { category_key: string; label: string },
  Parameters<typeof createCustomCategory>[0]
>("dataHubConsole/createCustomCategory", input => createCustomCategory(input));
export const createDataHubCustomMetric = createAsyncThunk<
  { metric_id: string; metric_name: string },
  Parameters<typeof createCustomMetric>[0]
>("dataHubConsole/createCustomMetric", input => createCustomMetric(input));

// QA audit
export const runDataHubQaAudit = createAsyncThunk<QaReport, void>(
  "dataHubConsole/runQaAudit",
  () => runQaAudit()
);

/**
 * Actions whose payload is deliberately not serializable (a File). The store
 * tells the serializability check to skip them rather than have every resume
 * log a warning; nothing keeps these payloads in state.
 */
export const NON_SERIALIZABLE_DATA_HUB_ACTIONS = [
  fetchDataHubStoredFile.fulfilled.type,
];
